import React from "react";
import { Check, Loader2 } from "lucide-react";
import { Journey } from "../sessionJourney";

/**
 * The steps an upload goes through, each done / in hand / still to come, with the frame count under
 * the step that is counting frames. Used on the processing card (before any result exists) and on
 * the Premium tab (while premium is on its way), so both tell the same story. `children` sit under
 * the steps — the frame strip, the on-device labelling step.
 */
export default function ProcessingJourney({
  journey,
  tone = "light",
  children,
}: {
  journey: Journey;
  /** "dark" for the black media card, "light" for a white card. */
  tone?: "light" | "dark";
  children?: React.ReactNode;
}) {
  const dark = tone === "dark";
  const percent = journey.framesTotal > 0 ? Math.round((journey.framesDone * 100) / journey.framesTotal) : 0;
  return (
    <div className="w-full max-w-md text-left" data-testid="processing-journey">
      <ol className="space-y-2">
        {journey.steps.map((step) => (
          <li key={step.key} className="flex items-start gap-2.5" data-testid={`journey-step-${step.key}`} data-state={step.state}>
            <span
              className={
                "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full " +
                (step.state === "done"
                  ? "bg-emerald-600 text-white"
                  : step.state === "active"
                    ? dark ? "bg-white/15 text-white" : "bg-slate-900 text-white"
                    : dark ? "bg-white/10 text-white/40" : "bg-slate-200 text-slate-400")
              }
            >
              {step.state === "done" ? (
                <Check className="h-3 w-3" />
              ) : step.state === "active" ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : null}
            </span>
            <span className="min-w-0 flex-1">
              <span
                className={
                  "block text-sm " +
                  (step.state === "upcoming"
                    ? dark ? "text-white/40" : "text-slate-400"
                    : step.state === "active"
                      ? dark ? "font-semibold text-white" : "font-semibold text-slate-900"
                      : dark ? "text-white/80" : "text-slate-600")
                }
              >
                {step.label}
              </span>
              {step.state === "active" && step.detail && (
                <span className={"block text-xs tabular-nums " + (dark ? "text-white/60" : "text-slate-500")}>{step.detail}</span>
              )}
              {step.state === "active" && (step.key === "analyse" || step.key === "cloud") && journey.framesTotal > 0 && (
                <span className={"mt-1 block h-1.5 overflow-hidden rounded-full " + (dark ? "bg-white/10" : "bg-slate-100")}>
                  <span className="block h-full bg-emerald-500 transition-all duration-500" style={{ width: `${percent}%` }} />
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
      {children}
    </div>
  );
}
